/**
 * Domain types for the core product entities.
 *
 * STEP 2: these now correspond 1:1 with the D1 tables created in
 * `database/migrations/0002_product_tables.sql` — field names/shapes here
 * should stay in sync with that migration. Nothing in the API actually
 * reads/writes these yet (routes still return `notImplemented`); that
 * wiring happens as each feature is built in later steps.
 *
 * Money is always an integer number of cents (never a float), matching the
 * `*_cents` columns in the schema.
 */

import type { OrganizationId } from "./tenancy";

export type LeadId = string;
/** Pipeline stage. (Step 12 renamed the stored values `converted` -> `won`
 * and `unqualified` -> `lost`; see migration 0007.) */
export type LeadStatus = "new" | "contacted" | "qualified" | "won" | "lost";
export const LEAD_STATUSES: readonly LeadStatus[] = ["new", "contacted", "qualified", "won", "lost"];
export type LeadSource = "missed_call" | "manual" | "other";

/** The qualification *decision* stored on a lead, distinct from its
 * pipeline `status`. The first three are the AI provider's vocabulary
 * (`LeadQualificationStatus`); `needs_human` means the system declined to
 * decide (AI uncertain, conflicting with the org's rules, or failed). */
export type LeadQualificationDecision = "qualified" | "unqualified" | "needs_more_info" | "needs_human";
export const LEAD_QUALIFICATION_DECISIONS: readonly LeadQualificationDecision[] = [
  "qualified",
  "unqualified",
  "needs_more_info",
  "needs_human",
];

/** Lead fields an organization can require before a lead may be
 * auto-qualified. */
export type QualificationRequiredField = "name" | "service" | "address" | "timeline";
export const QUALIFICATION_REQUIRED_FIELDS: readonly QualificationRequiredField[] = [
  "name",
  "service",
  "address",
  "timeline",
];

/**
 * Per-organization qualification rules, stored as JSON in
 * `automation_settings.qualification_rules`. All fields optional; omitted
 * fields fall back to defaults (see `DEFAULT_QUALIFICATION_RULES` in
 * apps/api/src/lib/lead-qualification.ts). The AI proposes; these rules
 * decide whether the proposal is accepted.
 */
export interface QualificationRules {
  /** Fields that must be known before a lead can be `qualified`. */
  requiredFields?: QualificationRequiredField[];
  /** If non-empty, the lead's service must contain one of these
   * (case-insensitive substring). */
  acceptedServices?: string[];
  /** If non-empty, the lead's address must contain one of these (city,
   * ZIP prefix, ...; case-insensitive substring). */
  serviceAreaKeywords?: string[];
  /** If set, an *estimated* value below this makes the lead a rule
   * violation. Unknown values never violate. */
  minEstimatedValueCents?: number;
}

export interface Lead {
  id: LeadId;
  organizationId: OrganizationId;
  name: string | null;
  phone: string;
  email: string | null;
  service: string | null;
  address: string | null;
  timeline: string | null;
  /** A guess (AI or person). Never revenue. */
  estimatedValueCents: number | null;
  /** Actual revenue a person recorded for a WON lead; null until confirmed. */
  confirmedRevenueCents: number | null;
  revenueConfirmedAt: string | null;
  status: LeadStatus;
  source: LeadSource;
  /** Untyped on purpose — shape depends on the future AI qualification
   * flow. Parse/validate it at the point of use, not here. */
  qualificationData: unknown | null;
  /** The qualification decision (null if never evaluated). */
  qualificationStatus: LeadQualificationDecision | null;
  /** Conversation / call this lead originated from, when known. */
  conversationId: string | null;
  callId: string | null;
  needsHuman: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CallId = string;
export type CallDirection = "inbound" | "outbound";
export type CallOutcome = "missed" | "answered" | "voicemail" | "failed";

export interface CallRecord {
  id: CallId;
  organizationId: OrganizationId;
  leadId: LeadId | null;
  direction: CallDirection;
  outcome: CallOutcome;
  fromNumber: string;
  toNumber: string;
  durationSeconds: number | null;
  externalProvider: string | null;
  externalCallId: string | null;
  occurredAt: string;
  createdAt: string;
}

export type ConversationId = string;
export type ConversationStatus = "active" | "closed";
export type ConversationChannel = "sms" | "other";

export interface Conversation {
  id: ConversationId;
  organizationId: OrganizationId;
  leadId: LeadId | null;
  status: ConversationStatus;
  channel: ConversationChannel;
  /** Set by the AI conversation engine (Step 11) once a human needs to
   * take over -- see `apps/api/src/lib/ai-conversation-engine.ts`. Never
   * automatically cleared once set. */
  needsHuman: boolean;
  createdAt: string;
  updatedAt: string;
}

export type MessageDirection = "inbound" | "outbound";
export type MessageSenderType = "human" | "ai" | "system";

export interface Message {
  id: string;
  conversationId: ConversationId;
  organizationId: OrganizationId;
  direction: MessageDirection;
  senderType: MessageSenderType;
  body: string;
  externalMessageId: string | null;
  sentAt: string;
}

/**
 * Documents the shape `automation_settings.operating_hours` is expected to
 * have when the missed-call automation (Step 10) reads it -- but the
 * column itself stays untyped JSON (see `AutomationSettings.operatingHours`
 * below), so this is a contract for application code to validate against,
 * not a schema the database enforces. `null`/absent for a day (or for the
 * whole object) means "no restriction for that day" / "no hours configured
 * at all" respectively -- the automation runs any time rather than being
 * silently disabled by an empty settings row. Hours are "HH:MM" 24-hour,
 * local to `timezone` (an IANA name, e.g. "America/New_York"; defaults to
 * "UTC" if omitted). No support for overnight ranges (close < open) --
 * a business open past midnight should split into two entries is out of
 * scope for Step 10's "basic" business-hours check.
 */
export interface OperatingHoursWindow {
  open: string;
  close: string;
}

export interface OperatingHours {
  timezone?: string;
  monday?: OperatingHoursWindow | null;
  tuesday?: OperatingHoursWindow | null;
  wednesday?: OperatingHoursWindow | null;
  thursday?: OperatingHoursWindow | null;
  friday?: OperatingHoursWindow | null;
  saturday?: OperatingHoursWindow | null;
  sunday?: OperatingHoursWindow | null;
}

/** Configurable automation behavior for one organization. One record per
 * organization (organizationId doubles as the row's key). Each JSON field
 * is left as `unknown` here — same reasoning as Lead.qualificationData. */
export interface AutomationSettings {
  organizationId: OrganizationId;
  missedCallSmsEnabled: boolean;
  missedCallSmsTemplate: string | null;
  aiEnabled: boolean;
  aiBehavior: unknown | null;
  businessInfo: unknown | null;
  qualifyingQuestions: unknown | null;
  /** Shape: `QualificationRules`. */
  qualificationRules: unknown | null;
  operatingHours: unknown | null;
  escalationRules: unknown | null;
  notificationSettings: unknown | null;
  createdAt: string;
  updatedAt: string;
}

/** One day of rollup metrics for one organization. `date` is a UTC
 * `YYYY-MM-DD`. Mirrors `AnalyticsMetrics` (see dashboard.ts) plus keys. */
export interface AnalyticsDailyRecord {
  organizationId: OrganizationId;
  date: string;
  totalCalls: number;
  answeredCalls: number;
  missedCalls: number;
  missedCallsFollowedUp: number;
  messagesSent: number;
  messagesReceived: number;
  aiConversations: number;
  leadsCreated: number;
  qualifiedLeads: number;
  convertedLeads: number;
  estimatedLeadValueCents: number;
  confirmedRevenueCents: number;
}
